import { colors } from './theme';
import { t } from './i18n';
import type { Role } from './auth';

// Mirrors App\Ops\FulfilmentFlow on the server (the server checks every rule again).
export const STAGE_TONE: Record<string, { bg: string; fg: string }> = {
  incoming: { bg: colors.warningTint, fg: colors.warning },
  new: { bg: colors.turquoiseTint, fg: colors.turquoiseDark },
  ready_to_invoice: { bg: colors.violetTint, fg: colors.violetDark },
  awaiting_prep: { bg: colors.violetTint, fg: colors.violetDark },
  awaiting_driver: { bg: colors.warningTint, fg: colors.warning },
  awaiting_pickup: { bg: colors.warningTint, fg: colors.warning },
  out_for_delivery: { bg: colors.turquoiseTint, fg: colors.turquoiseDark },
  driver_custody: { bg: colors.dangerTint, fg: colors.danger },
  awaiting_collector: { bg: colors.dangerTint, fg: colors.danger },
  collector_custody: { bg: colors.dangerTint, fg: colors.danger },
  closed: { bg: colors.successTint, fg: colors.success },
  cancelled: { bg: colors.greyTint, fg: colors.muted },
  returned: { bg: colors.greyTint, fg: colors.muted },
  with_carrier: { bg: colors.turquoiseTint, fg: colors.turquoiseDark },
  carrier_custody: { bg: colors.dangerTint, fg: colors.danger },
  carrier_returning: { bg: colors.warningTint, fg: colors.warning },
};

export type Action =
  | 'to_call' | 'reassign_call' | 'call_result' | 'invoiced' | 'prepared' | 'assign_driver' | 'handover' | 'delivered'
  | 'send_to_collector' | 'collector_confirmed' | 'handed_to_finance' | 'back' | 'cancel' | 'reject'
  | 'set_carrier' | 'hand_to_carrier' | 'carrier_update' | 'carrier_delivered' | 'carrier_returned' | 'return_received';

const ACTION_LABEL: Record<string, [string, string]> = {
  to_call: ['تحويل للكول سنتر', 'Forward to call center'],
  reassign_call: ['تغيير موظف الاتصال', 'Change call agent'],
  call_result: ['نتيجة الاتصال', 'Call result'],
  invoiced: ['تمت الفوترة', 'Invoiced'],
  prepared: ['تم التجهيز', 'Prepared'],
  assign_driver: ['تعيين سائق', 'Assign driver'],
  handover: ['تسليم الطلب للسائق', 'Hand over to driver'],
  delivered: ['تم التسليم للزبون', 'Delivered to customer'],
  send_to_collector: ['تسليم المبلغ للمحصّل', 'Hand cash to collector'],
  collector_confirmed: ['تأكيد استلام المبلغ', 'Confirm cash received'],
  handed_to_finance: ['تأكيد القبض وإغلاق الطلب', 'Confirm receipt and close'],
  back: ['إرجاع لمرحلة سابقة', 'Move back a stage'],
  cancel: ['إلغاء الطلب', 'Cancel order'],
  reject: ['رفض الزبون الاستلام', 'Customer refused'],
  enrolled: ['دخل الطلب للتطبيق', 'Order entered the app'],
  reopened: ['أعيد فتح الطلب', 'Order reopened'],
  outside: ['تغيير الحالة من صفحة الطلبات', 'Status changed on the Orders page'],
  settle_shipping: ['دفع أجور شحن', 'Shipping fees paid'],
  set_carrier: ['اختيار شركة الشحن', 'Choose shipping company'],
  hand_to_carrier: ['تسليم لشركة الشحن', 'Hand to shipping company'],
  carrier_update: ['تحديث رقم الوصل / حالة الشركة', 'Update waybill / company status'],
  carrier_delivered: ['سلّمت الشركة الطلب للزبون', 'Company delivered it'],
  carrier_returned: ['رفض الزبون، راجع عبر الشركة', 'Refused, coming back via company'],
  return_received: ['وصل المرتجع من الشركة', 'Return received from company'],
  carrier_settle: ['تسديد شركة الشحن', 'Shipping company paid'],
};

export const actionLabel = (a: string) => {
  const l = ACTION_LABEL[a];
  return l ? t(l[0], l[1]) : a;
};

export const SIDE_ACTIONS: Action[] = ['back', 'cancel', 'reject', 'carrier_returned'];

// stage → [action, roles that may do it]
// can_cancel / can_back = the "cancel orders" / "move orders back" permission switches.
// set_carrier: early by intake / call center (so the customer hears the right total), shipping any time before handover.
const RULES: Record<string, [Action, Role[]][]> = {
  incoming: [['to_call', ['intake']], ['set_carrier', ['intake', 'ship']], ['cancel', ['can_cancel']]], // new orders: the admin forwards them
  new: [['call_result', ['call']], ['reassign_call', ['intake']], ['set_carrier', ['call', 'ship']], ['cancel', ['can_cancel']], ['back', ['can_back']]],
  ready_to_invoice: [['invoiced', ['invoice']], ['set_carrier', ['ship']], ['cancel', ['can_cancel']], ['back', ['can_back']]],
  awaiting_prep: [['prepared', ['prep']], ['set_carrier', ['ship']], ['back', ['invoice_admin', 'can_back']], ['cancel', ['can_cancel']]],
  awaiting_driver: [['assign_driver', ['ship']], ['hand_to_carrier', ['ship']], ['cancel', ['can_cancel']], ['back', ['can_back']]],
  awaiting_pickup: [['handover', ['ship']], ['cancel', ['can_cancel']], ['back', ['can_back']]],
  out_for_delivery: [['delivered', ['driver']], ['reject', ['driver']], ['back', ['can_back']]],
  driver_custody: [['send_to_collector', ['driver']]],
  awaiting_collector: [['collector_confirmed', ['coll']]],
  collector_custody: [['handed_to_finance', ['finance']]],
  with_carrier: [['carrier_delivered', ['ship']], ['carrier_update', ['ship']], ['carrier_returned', ['ship']]],
  carrier_returning: [['return_received', ['ship']], ['carrier_update', ['ship']]],
  carrier_custody: [['carrier_update', ['ship']]], // paid from "Cash → shipping companies"
};

/** Stages after invoicing: changing the company changes the invoice total, so it must be confirmed. */
export const INVOICED_STAGES = ['awaiting_prep', 'awaiting_driver'];

/** Actions this account can take on an order at this stage (admin: all of them). */
export function actionsFor(stage: string, has: (...roles: Role[]) => boolean): Action[] {
  return (RULES[stage] ?? []).filter(([, roles]) => has(...roles)).map(([action]) => action);
}

export const callOutcomes = () => [
  { key: 'confirmed', label: t('رد وأكّد الطلب', 'Answered and confirmed') },
  { key: 'no_answer', label: t('لم يرد', 'No answer') },
  { key: 'postponed', label: t('طلب التأجيل', 'Asked to postpone') },
];

const ROLE_LABEL: Record<string, [string, string]> = {
  admin: ['مدير', 'Admin'],
  call: ['كول سنتر', 'Call center'],
  invoice: ['الفوترة', 'Invoicing'],
  invoice_admin: ['ادمن الفوترة', 'Invoicing admin'],
  prep: ['التجهيز', 'Preparation'],
  ship: ['الشحن', 'Shipping'],
  driver: ['سائق', 'Driver'],
  coll: ['المحصّل', 'Collector'],
  finance: ['ادمن القبض', 'Receipts admin'],
};

/** "Arabic invoice" / "Kurdish invoice" chip label. */
export const invoiceLabel = (lang: string) => (lang === 'kr' ? t('فاتورة كردي', 'Kurdish invoice') : t('فاتورة عربي', 'Arabic invoice'));

export const roleLabel = (r: string) => {
  const l = ROLE_LABEL[r];
  return l ? t(l[0], l[1]) : r;
};
